// ---------- combien de temps une feuille demande ----------
// L'unité de travail n'est PAS la case, c'est la ZONE. `flood` est actif et aucun interrupteur ne
// l'expose (src/settings.ts) : un tap remplit toute la région 4-connexe de même symbole. Une
// feuille à grands aplats se peint donc en quelques centaines de gestes là où une feuille tramée
// de mêmes dimensions en demande plusieurs milliers.
//
// MESURÉ sur trois puzzles de production, chronométrés par un joueur réel :
//
//   puzzle             alphabet         grille    à peindre   zones   mesuré   s/zone
//   Hamburger          pointilleRiche   81x145        4 636   1 883   17 min     0,54
//   Charlie Chaplin    motifsTrait      110x110       5 200   1 712   13 min     0,46
//   Jean Paul II       motifsFin        112x150      12 729   4 684   44 min     0,56
//
// Dispersion du rapport selon l'unité qu'on choisit : ZONES 9 %, cases à peindre 16 %,
// `gridW * gridH` 38 %. C'est ce qui justifie de stocker une colonne plutôt que de calculer
// l'estimation depuis les dimensions, qui sont pourtant déjà sur la carte : le prédicteur naïf se
// trompe quatre fois plus.
//
// Et les trois emploient trois alphabets DIFFÉRENTS pour un rapport qui tient entre 0,46 et 0,56 :
// il n'y a donc pas de coefficient par alphabet à introduire, et c'est une complication en moins.
export const SECONDS_PER_ZONE = 0.52;

/**
 * Le nombre de gestes qu'une grille demande : ses composantes 4-connexes de même code, les cases
 * de code 0 exclues — elles ne se peignent jamais (cf. `updateProgress()` dans render.ts).
 *
 * Le voisinage est celui de `floodFrom` (src/ui.ts) et doit le rester : si l'un des deux passait
 * aux huit voisins sans l'autre, l'estimation mentirait sans que rien ne casse.
 *
 * Parcours en largeur ITÉRATIF, sur un tableau de visite à plat : une grille peut faire 250 000
 * cases (500 x 500, la borne du stockage des taps), et une récursion y épuiserait la pile.
 */
export function countZones(target: readonly number[][]): number {
  const gh = target.length;
  const gw = target[0]?.length ?? 0;
  if (!gh || !gw) return 0;
  const seen = new Uint8Array(gw * gh);
  const queue = new Int32Array(gw * gh);
  let zones = 0;
  for (let r0 = 0; r0 < gh; r0++) {
    for (let c0 = 0; c0 < gw; c0++) {
      const code = target[r0][c0];
      if (code <= 0 || seen[r0 * gw + c0]) continue;
      zones++;
      let head = 0;
      let tail = 0;
      queue[tail++] = r0 * gw + c0;
      seen[r0 * gw + c0] = 1;
      while (head < tail) {
        const i = queue[head++];
        const r = (i / gw) | 0;
        const c = i - r * gw;
        if (r > 0 && !seen[i - gw] && target[r - 1][c] === code) {
          seen[i - gw] = 1;
          queue[tail++] = i - gw;
        }
        if (r + 1 < gh && !seen[i + gw] && target[r + 1][c] === code) {
          seen[i + gw] = 1;
          queue[tail++] = i + gw;
        }
        if (c > 0 && !seen[i - 1] && target[r][c - 1] === code) {
          seen[i - 1] = 1;
          queue[tail++] = i - 1;
        }
        if (c + 1 < gw && !seen[i + 1] && target[r][c + 1] === code) {
          seen[i + 1] = 1;
          queue[tail++] = i + 1;
        }
      }
    }
  }
  return zones;
}

/**
 * L'échelle sur laquelle une estimation est ARRONDIE, en minutes. Grossière à dessein : sur trois
 * mesures le rapport varie déjà de 9 %, et annoncer « 37 min » promettrait une précision qu'on n'a
 * pas. Sur ces paliers, les trois puzzles ci-dessus tombent dans le même seau que leur durée
 * réelle (15, 15, 45 contre 17, 13, 44).
 *
 * Le dernier palier est un PLANCHER, pas une valeur : 120 se lit « 2 h ou plus ». Au-delà, la
 * feuille est un projet de plusieurs séances et le chiffre exact n'apprend plus rien.
 */
const LADDER = [5, 10, 15, 20, 30, 45, 60, 90, 120] as const;

/**
 * L'estimation d'une feuille, en minutes calées sur l'échelle. `pct` est la progression déjà
 * faite : sans lui c'est la feuille entière, avec lui ce qu'il en reste.
 *
 * Le restant se déduit du pourcentage de CASES, pas de zones — le catalogue ne connaît que `pct`,
 * et il ne charge pas la grille pour l'affiner. L'approximation sous-estime un peu, les grandes
 * zones se peignant en premier, et c'est justement ce que l'arrondi grossier absorbe.
 *
 * Rend 0 quand il n'y a rien à annoncer : grille sans zones, ou travail terminé. L'appelant
 * n'affiche alors rien — un « 0 min » sur une carte ne veut rien dire.
 *
 * Rend un NOMBRE et jamais une chaîne : `shared/` est lu par l'administration, qui reste en
 * français, et par le jeu, qui parle cinq langues. La mise en forme appartient à src/i18n.
 */
export function estimateMinutes(zones: number, pct = 0): number {
  if (!Number.isFinite(zones) || zones <= 0) return 0;
  const left = Math.max(0, 1 - Math.max(0, Math.min(100, pct)) / 100);
  const minutes = (zones * SECONDS_PER_ZONE * left) / 60;
  if (minutes <= 0) return 0;
  // Le palier le plus PROCHE, et non le premier au-dessus : arrondir vers le haut ferait
  // annoncer 20 min pour la feuille mesurée à 17, alors que 15 est le bon seau. Sur les trois
  // mesures, le plus proche tombe juste à chaque fois.
  return LADDER.reduce((best, m) => (Math.abs(m - minutes) < Math.abs(best - minutes) ? m : best));
}

/**
 * La durée, mise en mots EN FRANÇAIS — pour l'administration, exactement comme `DIFFICULTIES`
 * porte ses libellés juste à côté (shared/difficulty.ts). `shared/` ne devient pas multilingue
 * pour autant : le jeu ne lit pas cette fonction, il a `fmtMinutes` dans src/i18n, qui rend la
 * même échelle dans ses cinq langues.
 *
 * Rend une chaîne vide quand il n'y a rien à annoncer, pour que l'appelant n'ait pas à tester
 * deux choses.
 */
export function effortLabel(minutes: number): string {
  if (minutes <= 0) return '';
  if (minutes >= 120) return `≈ ${Math.floor(minutes / 60)} h+`;
  if (minutes < 60) return `≈ ${minutes} min`;
  const h = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `≈ ${h} h ${rest}` : `≈ ${h} h`;
}
